import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from 'src/app/authentication/auth.service';
import { Usuario } from 'src/app/authentication/interfaces/interfaces';
import { MedicalRecordListItem } from 'src/app/interfaces/gestion-clinica/medical-record.interface';
import { MedicalRecordsService } from 'src/app/services/gestion-clinica/medical-records.service';
import { OrdenesService } from 'src/app/services/transacciones/ordenes.service';
import { buildHistoriaClinicaHtml } from '../shared/historia-clinica-print';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-historial-paciente',
  templateUrl: './historial-paciente.component.html',
  styleUrls: ['./historial-paciente.component.scss'],
  standalone: false
})
export class HistorialPacienteComponent implements OnInit {
  @ViewChild('modalEvolucion') modalEvolucion!: TemplateRef<any>;
  @ViewChild('modalEvoluciones') modalEvoluciones!: TemplateRef<any>;
  @ViewChild('modalOrdenes') modalOrdenes!: TemplateRef<any>;
  @ViewChild('modalAuditoria') modalAuditoria!: TemplateRef<any>;

  filtrosForm: FormGroup;
  evolutionForm: FormGroup;
  rows: MedicalRecordListItem[] = [];
  relatedOrders: any[] = [];
  evolutionRows: any[] = [];
  auditRows: any[] = [];
  selectedRecord?: MedicalRecordListItem;
  selectedPanelMode?: 'evolution' | 'orders' | 'evolutions' | 'audit';
  loadingEvolutions = false;
  loadingRelatedOrders = false;
  loadingAudit = false;
  loading = false;
  total = 0;
  limit = 10;
  offset = 0;
  searchTerm = '';
  private searchTimeout: any;
  private usuario: Usuario;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private medicalRecordsService: MedicalRecordsService,
    private router: Router,
    private modalService: NgbModal,
    private ordenesService: OrdenesService
  ) {
    this.usuario = this.authService.loginUsuario;
    const currentMonth = this.getCurrentMonthRange();
    this.filtrosForm = this.fb.group({
      paciente: [''],
      documento: [''],
      fechaInicio: [currentMonth.start],
      fechaFin: [currentMonth.end],
      medico: [''],
      sucursal: ['']
    });
    this.evolutionForm = this.fb.group({
      subjective: ['', Validators.required],
      objective: [''],
      assessment: [''],
      plan: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.medicalRecordsService.records({
      term: this.searchTerm,
      fechaInicio: this.filtrosForm.get('fechaInicio')?.value || '',
      fechaFin: this.filtrosForm.get('fechaFin')?.value || '',
      limit: this.limit,
      offset: this.offset,
      perfil: this.usuario.perfil || '',
      sucursal: this.usuario.sucursal || ''
    }).subscribe({
      next: (resp: any) => {
        this.loading = false;
        this.rows = resp?.rows || [];
        this.total = resp?.total || 0;
      },
      error: (err: any) => {
        this.loading = false;
        Swal.fire('Historial de Pacientes', err?.error?.message || err?.message || 'No se pudo consultar el historial.', 'error');
      }
    });
  }

  buscar(): void {
    this.offset = 0;
    this.load();
  }

  aplicarFechas(): void {
    this.offset = 0;
    this.load();
  }

  buscarMientrasEscribe(value: string): void {
    this.searchTerm = value;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.offset = 0;
      this.load();
    }, 300);
  }

  limpiar(): void {
    this.searchTerm = '';
    const currentMonth = this.getCurrentMonthRange();
    this.filtrosForm.patchValue({
      fechaInicio: currentMonth.start,
      fechaFin: currentMonth.end
    });
    this.offset = 0;
    this.load();
  }

  cambiarLimite(value: string): void {
    this.limit = Number(value) || 10;
    this.offset = 0;
    this.load();
  }

  primeraPagina(): void {
    this.offset = 0;
    this.load();
  }

  paginaAnterior(): void {
    this.offset = Math.max(this.offset - this.limit, 0);
    this.load();
  }

  paginaSiguiente(): void {
    if (this.offset + this.limit >= this.total) return;
    this.offset += this.limit;
    this.load();
  }

  ultimaPagina(): void {
    const lastPage = Math.max(Math.ceil(this.total / this.limit) - 1, 0);
    this.offset = lastPage * this.limit;
    this.load();
  }

  get paginaActual(): number {
    return Math.floor(this.offset / this.limit) + 1;
  }

  get desde(): number {
    if (!this.total) return 0;
    return this.offset + 1;
  }

  get hasta(): number {
    return Math.min(this.offset + this.rows.length, this.total);
  }

  get canViewAudit(): boolean {
    return this.authService.hasPermission('gestion_clinica.auditoria.read');
  }

  verPdf(row: MedicalRecordListItem): void {
    this.medicalRecordsService.record(row.id).subscribe({
      next: (resp: any) => {
        const record = resp?.row;
        const printWindow = window.open('', '_blank', 'width=1000,height=1100');
        if (!printWindow) {
          Swal.fire('Historia Clínica', 'El navegador bloqueó la ventana emergente del PDF.', 'warning');
          return;
        }
        this.medicalRecordsService.evolutions(row.id).subscribe({
          next: (evolutionsResp: any) => {
            const html = buildHistoriaClinicaHtml(
              {
                ...record,
                evolutions: evolutionsResp?.rows || [],
                order: {
                  order_number: record?.order_number,
                  invoice_number: record?.invoice_number,
                  branch_name: record?.branch_name
                }
              },
              { permitirImpresion: true }
            );
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.focus();
          },
          error: () => {
            printWindow.close();
            Swal.fire('Historia Clínica', 'No se pudieron cargar las evoluciones del informe.', 'error');
          }
        });
      },
      error: (err: any) => {
        Swal.fire('Historia Clínica', err?.error?.message || err?.message || 'No se pudo cargar la historia clínica.', 'error');
      }
    });
  }

  editar(row: MedicalRecordListItem): void {
    this.router.navigate(['/sistema/gestion-clinica/historial/editar', row.id]);
  }

  agregarEvolucion(row: MedicalRecordListItem): void {
    this.selectedRecord = row;
    this.selectedPanelMode = 'evolution';
    this.relatedOrders = [];
    this.evolutionForm.reset();
    this.modalService.open(this.modalEvolucion, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  guardarEvolucion(): void {
    if (!this.selectedRecord) return;
    if (this.evolutionForm.invalid) {
      this.evolutionForm.markAllAsTouched();
      Swal.fire('Evolución Clínica', 'Complete al menos el relato del paciente y el plan de manejo.', 'warning');
      return;
    }
    this.medicalRecordsService.addEvolution(this.selectedRecord.id, {
      ...this.evolutionForm.value,
      created_by_user_id: Number(this.usuario.idUsuario || 0)
    }).subscribe({
      next: () => {
        Swal.fire('Evolución Clínica', 'Evolución agregada correctamente.', 'success');
        this.selectedRecord = undefined;
        this.selectedPanelMode = undefined;
        this.evolutionRows = [];
        this.modalService.dismissAll();
      },
      error: (err: any) => {
        Swal.fire('Evolución Clínica', err?.error?.message || err?.message || 'No se pudo agregar la evolución.', 'error');
      }
    });
  }

  verEvoluciones(row: MedicalRecordListItem): void {
    this.selectedRecord = row;
    this.selectedPanelMode = 'evolutions';
    this.relatedOrders = [];
    this.evolutionRows = [];
    this.loadingEvolutions = true;
    this.modalService.open(this.modalEvoluciones, {
      centered: true,
      size: 'lg'
    });
    this.medicalRecordsService.evolutions(row.id).subscribe({
      next: (resp: any) => {
        this.loadingEvolutions = false;
        this.evolutionRows = resp?.rows || [];
      },
      error: (err: any) => {
        this.loadingEvolutions = false;
        Swal.fire('Evoluciones Clínicas', err?.error?.message || err?.message || 'No se pudieron cargar las evoluciones.', 'error');
      }
    });
  }

  verOrdenes(row: MedicalRecordListItem): void {
    this.selectedRecord = row;
    this.selectedPanelMode = 'orders';
    this.relatedOrders = [];
    this.evolutionRows = [];
    this.loadingRelatedOrders = true;
    this.modalService.open(this.modalOrdenes, {
      centered: true,
      size: 'lg'
    });
    this.medicalRecordsService.relatedOrders(row.patient_id).subscribe({
      next: (resp: any) => {
        this.loadingRelatedOrders = false;
        this.relatedOrders = resp?.rows || [];
      },
      error: () => {
        this.loadingRelatedOrders = false;
        this.relatedOrders = [];
      }
    });
  }

  verAuditoria(row: MedicalRecordListItem): void {
    this.selectedRecord = row;
    this.selectedPanelMode = 'audit';
    this.relatedOrders = [];
    this.evolutionRows = [];
    this.auditRows = [];
    this.loadingAudit = true;
    this.modalService.open(this.modalAuditoria, {
      centered: true,
      size: 'xl'
    });
    this.medicalRecordsService.audits(row.id).subscribe({
      next: (resp: any) => {
        this.loadingAudit = false;
        this.auditRows = resp?.rows || [];
      },
      error: (err: any) => {
        this.loadingAudit = false;
        Swal.fire('Auditoría Clínica', err?.error?.message || err?.message || 'No se pudo cargar la auditoría.', 'error');
      }
    });
  }

  cerrarPanel(): void {
    this.selectedRecord = undefined;
    this.selectedPanelMode = undefined;
    this.relatedOrders = [];
    this.evolutionRows = [];
    this.auditRows = [];
    this.loadingEvolutions = false;
    this.loadingRelatedOrders = false;
    this.loadingAudit = false;
    this.modalService.dismissAll();
  }

  abrirOrden(order: any): void {
    const pdfWindow = window.open('', '_blank');
    this.ordenesService.generarOrdenTransferencia(
      localStorage.getItem('token'),
      order?.order_number || '',
      order?.doctor_name || '',
      order?.doctor_phone || ''
    ).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        if (pdfWindow) {
          pdfWindow.location.href = url;
        } else {
          window.open(url, '_blank');
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      },
      error: () => {
        pdfWindow?.close();
        void Swal.fire('Atención', 'No se pudo generar la orden digital.', 'error');
      }
    });
  }

  private getCurrentMonthRange(): { start: string; end: string } {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: this.toInputDate(start),
      end: this.toInputDate(end)
    };
  }

  private toInputDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  auditActionLabel(action: string): string {
    const labels: { [key: string]: string } = {
      CREAR_HISTORIA_CLINICA: 'Creación de historia clínica',
      EDITAR_HISTORIA_CLINICA: 'Edición de historia clínica',
      AGREGAR_EVOLUCION: 'Registro de evolución'
    };
    return labels[action] || action || '-';
  }
}
